import { Table, TableHeader, TableRow, TableHeaderCell, TableCell, TableColumnSizingOptions, useTableFeatures, useTableColumnSizing_unstable, createTableColumn, TableColumnDefinition, MenuItem } from "@fluentui/react-components";
import React from "react";
import { TableBodyWithLoading } from "s365-dashboard-v2-file-picker";
import ShowLocalTime from "../../components/show-local-time/show-local-time.component";
import { ShareDefinitionSourceType, ShareWithMeFilterResponseModel } from "../../swagger-clients/s365-dashboard-v2-api-clients.service";
import { useLoading } from "../../utils/loading-indicator.component";
import { processServerError } from "../../utils/helpers/error.helper";
import { getSharingClient } from "../../services/dispatcher.service";
import { getDashboardSharingClient } from "../../services/dashboard.service";
import { FileDisplayName } from "../../components/file-display-name/file-display-name.component";
import { ShareFileModal } from "../../files/share/share-file-modal.component";
import { ContextMenu, ContextMenuType } from "../../files/context-menu/context-menu.component";
import { PeopleAddRegular } from "@fluentui/react-icons";
import { PersonDisplay } from "../../components/person-display/person-display.component";
import { FilterDetailsModal } from "../../filters/filter-details-modal/filter-details-modal.component";
import { HubConnection } from "@microsoft/signalr";

type SharedWithMeFiltersTableProps = {
    hubConnection: HubConnection;
}

export const SharedWithMeFiltersTable: React.FC<SharedWithMeFiltersTableProps> = (props) => {

    const [isLoading, loadingService] = useLoading();
    const [selectedItem, setSelectedItem] = React.useState<ShareWithMeFilterResponseModel>();
    const [showDetailsModal, setShowDetailsModal] = React.useState<boolean>(false);
    const [items, setItems] = React.useState<ShareWithMeFilterResponseModel[]>([]);
    const contextMenuRef = React.createRef<ContextMenuType>();

    React.useEffect(() => {
        getSharedFilters();
    }, []);


    const [columnSizingOptions] = React.useState<TableColumnSizingOptions>({
        name: {
            minWidth: 100,
            defaultWidth: 400,
        },
        flowsheet: {
            minWidth: 300,
            defaultWidth: 400
        },
        sharedBy: {
            minWidth: 200,
            defaultWidth: 300
        },
        created: {
            minWidth: 100,
            defaultWidth: 200

        }
    });


    const getSharedFilters = async () => {
        loadingService.showLoading("Loading filters...", async (hideMessage) => {

            try {
                const client = getDashboardSharingClient();
                const filtersResp = await client.getSharedWithMeFilters();
                setItems(filtersResp ?? []);

            } catch (error) {
                processServerError(error, undefined, "An error occurred while getting filters that are shared with me.");

            } finally {
                hideMessage();
            }

        });

    }

    const isColumnCentered = (columnId: string) => {

        switch (columnId) {
            case "shared": return true;

            default: return false;
        }
    }

    const { getRows, columnSizing_unstable, tableRef } = useTableFeatures<ShareWithMeFilterResponseModel>(
        {
            columns,
            items,
        },
        [useTableColumnSizing_unstable({ columnSizingOptions })]
    );


    return <div>
        <Table ref={tableRef} as="table" {...columnSizing_unstable.getTableProps()} >
            <TableHeader>
                <TableRow>
                    {columns.map((column) => (
                        <TableHeaderCell
                            onDragStart={e => {
                                e.preventDefault();
                                e.stopPropagation();
                            }}
                            className={`${isColumnCentered(column.columnId as string) ? 'files-table__cell--center' : ''} files-table__cell--bold`}
                            {...columnSizing_unstable.getTableHeaderCellProps(
                                column.columnId
                            )}
                        >
                            {column.renderHeaderCell()}
                        </TableHeaderCell>
                    ))}
                </TableRow>
            </TableHeader>
            <TableBodyWithLoading isLoading={isLoading}
                columnCount={4} loadingMessage="Loading..."
                itemCount={items ? items.length : 0}
                noItemsMessage="No items found.">
                {items && items.length > 0 && items.map((item) => {
                    return <TableRow
                        key={`scenario-${item.id}`}
                        style={{ cursor: "pointer" }}
                        onClick={() => { setSelectedItem(item); setShowDetailsModal(true); }}
                        className={selectedItem && selectedItem.id == item.id ? "table-row-selected" : undefined}
                        onContextMenu={(ev) => { ev.preventDefault(); setSelectedItem(item); contextMenuRef.current?.showMenu(ev); }}
                    >
                        {columns.map((column) => (
                            <TableCell
                                className={`${isColumnCentered(column.columnId as string) ? ' column--center' : ''}`}
                                {...columnSizing_unstable.getTableCellProps(column.columnId)}
                            >
                                {column.renderCell(item)}
                            </TableCell>
                        ))}

                    </TableRow>
                })}


            </TableBodyWithLoading>
        </Table>
        {showDetailsModal && selectedItem && <FilterDetailsModal
            filterId={selectedItem.id}
            filterVersionId={selectedItem.currentVersionId}
            filterName={selectedItem.name}
            fileUniqueId={selectedItem.fileUniqueIdentifier}
            fileVersionNumber={selectedItem.fileVersionNumber}
            isOpened={true}
            hubConnection={props.hubConnection}
            onClose={() => { setShowDetailsModal(false); }}
        />}
    </div>
}
const columns: TableColumnDefinition<ShareWithMeFilterResponseModel>[] = [
    createTableColumn<ShareWithMeFilterResponseModel>({
        columnId: "name",
        renderHeaderCell: () => <>Name</>,
        renderCell: (item: ShareWithMeFilterResponseModel) => {
            return <span>{item.name}</span>;

        }
    }),
    createTableColumn<ShareWithMeFilterResponseModel>({
        columnId: "flowsheet",
        renderHeaderCell: () => <>Flowsheet</>,
        renderCell: (item: ShareWithMeFilterResponseModel) => {
            return <FileDisplayName fileUniqueIdentifier={item.fileUniqueIdentifier} fileVersionNumber={item.fileVersionNumber} hidePath />;

        }
    }),
    createTableColumn<ShareWithMeFilterResponseModel>({
        columnId: "sharedBy",
        renderHeaderCell: () => <>Shared by</>,
        renderCell: (item: ShareWithMeFilterResponseModel) => {

            return <PersonDisplay id={`person-${item.owner?.id}`}
                name={item.owner?.displayName}
                userPrincipalName={item.owner?.userPrincipalName} />;
        }
    }),

    createTableColumn<ShareWithMeFilterResponseModel>({
        columnId: "shared",
        renderHeaderCell: () => <>Shared</>,
        renderCell: (item: ShareWithMeFilterResponseModel) => {

            return <ShowLocalTime date={item.sharedAtUtc} multiline />;
        }
    })
];